/**
 * Unified conversion tracking.
 *
 * Every meaningful action fires in BOTH destinations at once:
 *   - GA4        (gtag 'event', measurement id loaded in public/index.html)
 *   - Meta Pixel (fbq, base pixel loaded in public/index.html)
 * plus the Google Ads conversion where one exists.
 *
 * Why this file exists: metaPixel.js and googleAds.js were only ever wired
 * into the signup screen, and GA4 received no events at all — so GA4 reported
 * zero key events on every landing page and no channel could be credited with
 * a registration or a rupee. Call the helpers here instead of calling gtag or
 * fbq directly, so a new event never lands in one system only.
 */

import {
  trackCompleteRegistrationAsync,
  trackInitiateCheckout,
  trackPurchase as pixelPurchase,
  trackCustom as pixelCustom,
} from "./metaPixel.js";
import { trackGoogleAdsRegistration } from "./googleAds.js";

const ga = (eventName, params = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
};

/** Fired once a registration is persisted by the API. Awaitable: the caller
 *  usually redirects immediately afterwards and the beacon needs a moment. */
export const trackSignUp = async ({ plan = null, gender = null } = {}) => {
  ga("sign_up", { method: "email", plan: plan || undefined, gender: gender || undefined });
  trackGoogleAdsRegistration();
  await trackCompleteRegistrationAsync({ content_name: "signup", status: true });
};

/** Fired when a member opens the bank-transfer / checkout sheet for a plan. */
export const trackBeginCheckout = ({ plan, value = null, currency = "PKR" } = {}) => {
  ga("begin_checkout", {
    currency,
    value: value ?? undefined,
    items: plan ? [{ item_id: plan, item_name: plan }] : undefined,
  });
  trackInitiateCheckout({
    content_name: plan,
    currency,
    value: value ?? undefined,
  });
};

/** Fired when the member has submitted a transfer receipt — intent is real,
 *  money is not confirmed yet. Deliberately NOT a purchase. */
export const trackCheckoutSubmitted = ({ plan, value = null, currency = "PKR" } = {}) => {
  ga("checkout_receipt_submitted", { plan, currency, value: value ?? undefined });
  pixelCustom("ReceiptSubmitted", { content_name: plan, currency, value: value ?? undefined });
};

const PURCHASE_KEY = "sp_tracked_purchases";

const alreadyTracked = (id) => {
  if (typeof window === "undefined" || !id) return false;
  try {
    const seen = JSON.parse(window.localStorage.getItem(PURCHASE_KEY) || "[]");
    return seen.includes(String(id));
  } catch {
    return false;
  }
};

const rememberTracked = (id) => {
  if (typeof window === "undefined" || !id) return;
  try {
    const seen = JSON.parse(window.localStorage.getItem(PURCHASE_KEY) || "[]");
    seen.push(String(id));
    window.localStorage.setItem(PURCHASE_KEY, JSON.stringify(seen.slice(-50)));
  } catch {
    /* storage disabled — worst case we fire once per session */
  }
};

/**
 * Fired the first time this browser observes that a payment has been
 * completed. Bank transfers are approved by an admin hours later, so the
 * member is usually nowhere near the success page at the moment the money is
 * confirmed — tracking only on /payment-success loses almost every purchase.
 * Deduped per payment id via localStorage.
 *
 * Note: this is still browser-side and misses members who never return.
 * A server-side Conversions API / Measurement Protocol call on payment
 * approval is the complete fix.
 */
export const trackPurchaseOnce = ({
  paymentId,
  plan,
  amountCents = 0,
  currency = "PKR",
} = {}) => {
  const id = paymentId || `${plan}:${amountCents}`;
  if (alreadyTracked(id)) return false;

  const value = (Number(amountCents) || 0) / 100;
  const upper = (currency || "PKR").toUpperCase();

  ga("purchase", {
    transaction_id: String(id),
    currency: upper,
    value,
    items: plan ? [{ item_id: plan, item_name: plan, price: value, quantity: 1 }] : undefined,
  });
  pixelPurchase({ content_name: plan, currency: upper, value });

  rememberTracked(id);
  return true;
};

/** A member spent a view/credit and saw a contact — the core value moment. */
export const trackContactReveal = ({ usedSuperReveal = false } = {}) => {
  ga("contact_reveal", { method: usedSuperReveal ? "super_reveal" : "plan_view" });
  pixelCustom("ContactReveal", {
    content_name: usedSuperReveal ? "super_reveal" : "plan_view",
  });
};

/**
 * A click on a conversion CTA placed on an SEO page (blog post, city or
 * keyword landing page). Lets GA4 show which organic pages actually push
 * visitors towards signup, pricing or WhatsApp — before this, organic pages
 * showed sessions but no way to see whether anyone acted on them.
 */
export const trackCtaClick = ({ placement, target, page } = {}) => {
  ga("seo_cta_click", {
    placement: placement || undefined,
    cta_target: target || undefined,
    page_path:
      page || (typeof window !== "undefined" ? window.location.pathname : undefined),
  });
  if (target === "whatsapp" || target === "call") {
    ga("generate_lead", { method: target, placement: placement || undefined });
  }
};

export default {
  trackSignUp,
  trackBeginCheckout,
  trackCheckoutSubmitted,
  trackPurchaseOnce,
  trackContactReveal,
  trackCtaClick,
};
